/**
 * First-run setup: shown once, the first time Nova opens a new library.
 *
 * It offers the optional plugins bundled with Nova, each in its own words (the
 * engine half's description), so a plain music player stays plain and anyone
 * who needs more turns it on. The answer is stored by the engine
 * (`setup.done`); the Plugin manager changes it later. A plugin that adds a
 * layout (Radio) is switched to once it is on.
 */

import { useEffect, useState } from "react";
import { engineBase } from "../api";
import { useApp } from "../core/app";
import { syncPlugins, type EnginePlugin } from "../core/plugins";
import { registry } from "../core/registry";
import { Icon } from "./Icons";
import { toast } from "./Toast";

interface SetupState {
  done: boolean;
  plugins: EnginePlugin[];
}

export function SetupDialog() {
  const app = useApp();
  const [setup, setSetup] = useState<SetupState | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (app.offline) return;
    let live = true;
    fetch(`${engineBase()}/api/setup`)
      .then((r) => (r.ok ? r.json() : null))
      .then((s: SetupState | null) => live && s && setSetup(s))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [app.offline]);

  if (!setup || setup.done) return null;

  const finish = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${engineBase()}/api/setup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plugins: [...chosen] }),
      });
      const body = (await res.json()) as { error?: string; errors?: Record<string, string> };
      if (body.error) throw new Error(body.error);
      await syncPlugins();
      const failed = Object.entries(body.errors ?? {});
      if (failed.length) toast(`Could not turn on ${failed.map(([id]) => id).join(", ")}: ${failed[0][1]}`, "bad");
      else {
        // A plugin that brings a layout of its own is what the person chose it for.
        const turnedOn = setup.plugins.filter((p) => chosen.has(p.id));
        const withLayout = turnedOn.map((p) => ({ p, layout: registry.layoutsOf(p.id)[0] })).find((x) => x.layout);
        if (withLayout) {
          app.setLayout(withLayout.layout.id);
          toast(`${withLayout.p.name} is on. Switch layouts at the top right; change plugins any time in Plugins.`, "ok");
        }
      }
      setSetup({ ...setup, done: true });
    } catch (err) {
      toast(`Setup could not be saved: ${err instanceof Error ? err.message : String(err)}`, "bad");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string) =>
    setChosen((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="nv-modal" role="presentation">
      <div className="nv-dialog nv-box nv-setup" role="dialog" aria-modal="true" aria-label="Welcome to Nova">
        <header>
          <h2>Welcome to Nova</h2>
        </header>
        <div className="nv-box-body">
          <p>Nova plays and organises your music, and reads each song's BPM, key and energy from the audio. Add anything else you need:</p>
          {setup.plugins.length === 0 && <p className="nv-dim">No extras are available in this copy of Nova.</p>}
          <ul className="nv-setup-list">
            {setup.plugins.map((p) => (
              <li key={p.id}>
                <label className={chosen.has(p.id) ? "on" : ""}>
                  <Icon name={(p.icon as Parameters<typeof Icon>[0]["name"]) ?? "plug"} size={22} />
                  <span className="nv-setup-text">
                    <b>{p.name}</b>
                    <span>{p.description}</span>
                  </span>
                  <span className="nv-toggle">
                    <input type="checkbox" role="switch" checked={chosen.has(p.id)} onChange={() => toggle(p.id)} aria-label={`Add ${p.name}`} />
                    <i aria-hidden="true" />
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <p className="nv-dim">You can add or remove these later in Plugins.</p>
        </div>
        <footer className="nv-box-foot">
          <span className="nv-spacer" />
          <button className="nv-btn primary" disabled={busy} onClick={() => void finish()} autoFocus>
            {busy ? "Setting up…" : "Start"}
          </button>
        </footer>
      </div>
    </div>
  );
}
